import { SearchParameters } from 'algoliasearch-helper'
// import { history } from 'instantsearch.js/es/lib/routers'
import { get } from 'lodash'
import {
  type GetStaticPaths,
  type GetStaticProps,
  type NextPageWithLayout,
} from 'next'
import React, { useMemo } from 'react'
// import { renderToString } from 'react-dom/server'
import {
  //  getServerState,
  InstantSearch,
  type InstantSearchServerState,
  InstantSearchSSRProvider,
} from 'react-instantsearch'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { getPublicLayout } from '@public/components/PublicLayout'
import { ShopPageLayout } from 'components/InstantSearch'
import { BRAND_PAGE_SORT_OPTIONS } from 'components/InstantSearch/TypeSenseSortBy'
import { Seo } from 'components/Seo'
import { SHOWROOM_BRANDS_PAGE_SLUG } from 'constants/common'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { getTypesenseConfigurationOptions } from 'utils/typesense'
import { getServerClient } from 'utils/typesenseServer'

const { searchClient } = new TypesenseInstantSearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'title,embedding',
    include_fields:
      'title,imageUrl,imageAlt,minPrice,maxPrice,minTradePrice,maxTradePrice,minTradeDiscount,legacyProductId,breakoutOptionCount,carouselVariants,legacyProductVariantId,brand,availabilityOrder',
    sort_by: BRAND_PAGE_SORT_OPTIONS[0].value.replace('products-v3/sort/', ''),
  },
})

interface BrandPageProps {
  slug: string
  brandName: string
  serverState?: InstantSearchServerState
}

const InstantSearchWrapper: React.FC<
  {
    children: React.ReactNode
  } & Omit<
    React.ComponentProps<typeof InstantSearch>,
    'searchClient' | 'indexName'
  >
> = ({ children, ...rest }) => (
  <InstantSearch searchClient={searchClient} indexName="products-v3" {...rest}>
    {children}
  </InstantSearch>
)

export const BrandPage: NextPageWithLayout<BrandPageProps> = ({
  slug,
  brandName,
  serverState,
}) => {
  const realSlug = slug.split('/').pop()

  const searchParameters = useMemo(
    () =>
      new SearchParameters({
        hitsPerPage: 32,
        filters: `brandSlug:=${realSlug} && status:=PUBLISHED && isWhiteLabel:=false`,
      }),
    [realSlug]
  )

  return (
    <>
      <Seo
        title={`Shop ${brandName} at The Expert`}
        description={`Shop contemporary and vintage items from ${brandName}, curated by the world's top interior designers.`}
      />
      <InstantSearchSSRProvider {...serverState}>
        <InstantSearchWrapper routing>
          <ShopPageLayout
            searchParameters={searchParameters}
            showParentLevel={false}
          />
        </InstantSearchWrapper>
      </InstantSearchSSRProvider>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const client = getServerClient()

  const searchResponse = await client
    .collections<{ brand: string; brandSlug: string }>('products-v3')
    .documents()
    .search({
      q: '*',
      group_by: 'brand',
      group_limit: 1,
      filter_by: 'status:=PUBLISHED && isWhiteLabel:=false',
      include_fields: ['brand', 'brandSlug'],
      limit: 250,
    })
  const brands = searchResponse.grouped_hits?.map((c) => c.hits[0].document)

  const paths = brands.map(({ brandSlug }) => ({
    params: {
      slug: brandSlug,
    },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export const cleanEmpty = (obj: any, defaults = [undefined, null, NaN, '']) => {
  if (defaults.includes(obj)) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj
      .map((v) => (v && typeof v === 'object' ? cleanEmpty(v, defaults) : v))
      .filter((v) => !defaults.includes(v))
  }

  return Object.entries(obj).length
    ? Object.entries(obj)
        .map(([k, v]) => [
          k,
          v && typeof v === 'object' ? cleanEmpty(v, defaults) : v,
        ])
        .reduce(
          (a, [k, v]) => (defaults.includes(v) ? a : { ...a, [k]: v }),
          {}
        )
    : obj
}

export const getStaticProps: GetStaticProps = async (context) => {
  const brandSlug = get(context, ['params', 'slug'])
  const slug = `${SHOWROOM_BRANDS_PAGE_SLUG}/${brandSlug}`

  const baseProps = await prepareGetStaticNavigationProps()
  const client = getServerClient()

  const brandSearchResponse = await client
    .collections<{ brand: string }>('products-v3')
    .documents()
    .search({
      q: brandSlug.toString(),
      query_by: 'brandSlug',
      group_by: 'brand',
      filter_by: 'status:=PUBLISHED && isWhiteLabel:=false',
      group_limit: 1,
      include_fields: ['brand'],
    })

  const brand = brandSearchResponse?.grouped_hits[0]?.group_key[0]
  // const ssrRouter = history({
  //   getLocation() {
  //     return {
  //       ancestorOrigins: null,
  //       hash: '',
  //       host: 'theexpert.com',
  //       port: '443',
  //       protocol: 'https:',
  //       hostname: 'theexpert.com',
  //       href: `https://theexpert.com/shop/brands/${brandSlug}`,
  //       origin: 'https://theexpert.com',
  //       pathname: `shop/brands/${brandSlug}`,
  //       search: '',
  //       assign: () => {},
  //       reload: () => {},
  //       replace: () => {},
  //     }
  //   },
  // })

  // const serverState = await getServerState(
  //   <InstantSearchWrapper routing={{ router: ssrRouter }}>
  //     <div />
  //   </InstantSearchWrapper>,
  //   {
  //     renderToString,
  //   }
  // )

  return {
    props: {
      ...baseProps.props,
      slug,
      brandName: brand,
      breadcrumbs: [
        { label: 'Shop', link: '/shop' },
        { label: 'Brands', link: '/shop/brands' },
        { label: brand, link: brandSlug, disabled: true },
      ],
      // serverState: cleanEmpty(serverState),
    },
    revalidate: 3600,
    notFound: !brandSlug || baseProps.notFound,
  }
}

BrandPage.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true, isFullWidth: true })
